import React, { useEffect, useRef, useState } from 'react';

import { Button } from './../buttons';
import { Modal } from '../modal';
import { PauseIcon as PauseIconFA } from '../../lib/fontawesome/PauseIcon';
import PlayIcon from '../../lib/svg/PlayIcon';
import { PlayIcon as PlayIconFA } from '../../lib/fontawesome/PlayIcon';
import RichText from './../../lib/contentful/rich-text';
import Overlay from '../blocks/overlay';

import Form from '../Form';

import * as styles from './index.module.scss';

const RequestACallbackFormFields = [
  {
    legend: "Your details",
    inputs: [
      [
        {
          type: "text",
          label: "First name",
          name: "firstName",
          placeholder: "Joe",
          required: true
        },
        {
          type: "text",
          label: "Last name",
          name: "lastName",
          placeholder: "Bloggs",
          required: true
        },
      ],
      [
        {
          type: "email",
          label: "Email",
          name: "email",
          placeholder: "joe.bloggs@gmail.com",
          required: true
        },
        {
          type: "phone",
          label: "Contact Number",
          name: "phone",
          placeholder: "012345678910",
          required: true
        },
      ],
      [
        {
          type: "text",
          label: "House Number/Name",
          name: "houseNumber",
          placeholder: "1",
          required: true
        },
        {
          type: "text",
          label: "Postcode",
          name: "postcode",
          placeholder: "HU5 4JN",
          required: true
        }
      ]
    ]
  }
];

RequestACallbackFormFields.push(
  {
    type: 'button',
    content: 'Send'
  }
);

export const HeroBlock = ({
  backgroundColor,
  imageType,
  previewVideo,
  video,
  image,
  imageAlt,
  titleIcon,
  title,
  paragraphIsJsx,
  paragraph,
  ctaDownload,
  ctaLink,
  ctaText,
  spaceBottom,
  textColour,
  youtubeEmbedThumbnail,
  youTubeEmbedUrl,
  externalContent,
  overlayColour,
  openInANewTab = true,
  belowHeroCtaLink,
  belowHeroCtaText,
  belowHeroCtaIntro,
  belowHeroDownloadLink,
  requestACallbackCta,
  requestACallbackFields = false,
  noH1 = false,
  hide = false,
  showReadMoreButton,
}) => {
  const contentWrapper = useRef(null);
  const mainVideoRef = useRef(null);
  const placeholderVideoRef = useRef(null);

  const [active, setActive] = useState(false);
  const [requestACallbackModelActive, setRequestACallbackModelActive] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(false);

  const [videoHeight, setVideoHeight] = useState(0);

  useEffect(() => {
    // Handler for grabbing the height of the placeholder video on load with the 
    // intention of matching the height of the main video to the placeholder initially
    const handleLoad = () => {
      if (placeholderVideoRef.current)
        setVideoHeight(placeholderVideoRef.current.offsetHeight);
    };

    if (placeholderVideoRef.current)
      placeholderVideoRef.current.addEventListener('loadedmetadata', handleLoad);

    // Start and stop the video depending on if it's playing or not
    if (playingVideo && mainVideoRef.current && mainVideoRef.current.paused) {
      mainVideoRef.current.scrollIntoView();
      mainVideoRef.current.play();

      setTimeout(() => {
        // Set the max-height to the height of the screen to allow the transition to run
        mainVideoRef.current.style.maxHeight = '100vh';
      }, 100);
    } else if (!playingVideo && mainVideoRef.current && !mainVideoRef.current.paused) {
      mainVideoRef.current.pause();
    }

    return () => {
      if (placeholderVideoRef.current)
        placeholderVideoRef.current.removeEventListener('loadedmetadata', handleLoad);
    }
  }, [playingVideo])

  if (imageType === false)
    imageType = styles.sideImage;

  const CTA = ({ download, link }) => {
    var ctaLink;
    if (download && typeof download !== 'undefined') {
      ctaLink = download.publicUrl;
    } else if (link && typeof link !== 'undefined') {
      ctaLink = link;
    }

    if (ctaText && ctaText !== null && ctaLink && ctaLink !== null)
      return (
        <Button
          colorData={`green`}
          typeData={`default`}
          href={ctaLink}
          content={ctaText}
          alt={ctaText}
          opensNewTab={openInANewTab == null ? true : openInANewTab}
        />
      );
  }

  const RequestACallbackCTA = () => {
    if (!requestACallbackCta)
      return null;
    return (
      <Button
        elementType={'button'}
        clickEvent={e => setRequestACallbackModelActive(true)}
        content="Request a callback"
      />
    );
  }

  const Video = ({ image, previewVideo, video }) => {
    var hasPreview = previewVideo && typeof previewVideo === 'object';
    var hasVideo = video && typeof video === 'object';

    // Assign the main video by default, and switch to the preview if provided
    let mainVideo = video;
    if (hasPreview)
      mainVideo = previewVideo;

    let videoJSX = (
      <video
        className={styles.video}
        autoPlay={true}
        controls={false}
        allowFullScreen={false}
        loop
        muted
        poster={(image && typeof image === 'object') ? image?.url : null}
        ref={placeholderVideoRef}
        playsInline={true}
        autoBuffer={true}
      >
        <source src={mainVideo?.url} type={mainVideo?.file.contentType}></source>
      </video>
    );

    // Return the standalone video if there is no second video to play in a modal
    if (!(hasPreview && hasVideo) && (hasPreview || hasVideo))
      return videoJSX;

    if (hasPreview && hasVideo) {
      return (
        <div className={`${styles.videoContainer} ${playingVideo ? styles.videoContainerPlaying : ''}`}>
          {playingVideo
            ? (
              <video className={styles.videoContainerFull} ref={mainVideoRef} style={{ maxHeight: videoHeight }}>
                <source src={video.url} type={video.file.contentType}></source>
              </video>
            ) : videoJSX}
          <div className={styles.videoContainerPlay} onClick={() => setPlayingVideo(!playingVideo)}>
            {playingVideo
              ? <PauseIconFA />
              : <PlayIconFA />
            }
          </div>
        </div>
      );
    }
  }

  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef(null);

  const toggleExpanded = () => setExpanded(prev => !prev);

  useEffect(() => {
    if (showReadMoreButton) {
      const checkContentHeight = () => {
        if (contentRef.current) {
          const contentHeight = contentRef.current.scrollHeight;
          const lineHeight = parseInt(window.getComputedStyle(contentRef.current).lineHeight);
          const maxHeightFor3Lines = lineHeight * 3;

          // Only show the button if content exceeds 3 lines
          if (contentHeight > maxHeightFor3Lines) {
            setExpanded(false);
          } else {
            setExpanded(true);
          }
        }
      };

      checkContentHeight();
      window.addEventListener('resize', checkContentHeight); // Re-check on resize

      return () => window.removeEventListener('resize', checkContentHeight);
    }
  }, [paragraph, showReadMoreButton]);

  return (
    <>
      {!hide && (
        <div
          className={styles.hero + " " + styles[textColour] + " " + (spaceBottom ? styles.marginBottom : '')}
          style={{ backgroundColor: backgroundColor }}
        >
          {(image && typeof image === 'object') && <img className={`${styles.image} ${imageType}`} src={image.url} alt={image?.title ? image.title : imageAlt} />}
          <Overlay overlayColour={overlayColour} />
          <Video image={image} previewVideo={previewVideo} video={video} allowFullScreen={false} />
          <div
            className={`
              ${styles.contentWrapper} 
              ${previewVideo && typeof previewVideo === 'object' && !paragraph ? styles.contentWrapperAbsolute : ''}
              ${playingVideo ? styles.contentWrapperHidden : ''}
            `}
            ref={contentWrapper}
          >
            <div className={styles.title}>
              {(titleIcon && typeof titleIcon === 'object') ?
                <img className={styles.icon} src={titleIcon.url} />
                : null}
              {title && (
                noH1 == false ? <h1 className={styles.heading}>{title}</h1> : <h2 className={styles.heading}>{title}</h2>
              )}
            </div>
            <div ref={contentRef} className={`${styles.content} ${!expanded && showReadMoreButton ? `${styles.contentHidden} contentHidden` : ''}`}            >
              {paragraphIsJsx ? paragraph : (paragraph && typeof paragraph === 'object') ?
                RichText(paragraph)
                : (paragraph && typeof paragraph === 'string') ? <p>{paragraph}</p> : null}
              <CTA download={ctaDownload} link={ctaLink} />
              <RequestACallbackCTA />
              {externalContent}
            </div>
            {showReadMoreButton && (
              <Button clickEvent={e => {
                e.preventDefault();
                toggleExpanded();
                }}
                content={expanded ? 'Read less' : 'Read more'}
                elementType={'button'}
                additionalClasses="readMoreButton"
              ></Button>
            )}
            <noscript
              dangerouslySetInnerHTML={{
                __html: `
                  <style>
                    .contentHidden {
                      -webkit-line-clamp: unset;
                    }

                    .readMoreButton {
                      display: none;
                    }
                  </style>
                `,
              }}
            />
          </div>
          {!playingVideo
            ?
            (youtubeEmbedThumbnail && youTubeEmbedUrl)
              ?
              <>
                <div className={styles.sideVideo} onClick={() => setActive(!active)} style={{
                  backgroundImage: ((youtubeEmbedThumbnail && typeof youtubeEmbedThumbnail === 'object' && youtubeEmbedThumbnail.url.length > 0) && `url(${youtubeEmbedThumbnail.url})`)
                }}>
                  <span className={styles.videoCardPlayButton}>
                    <PlayIcon hover />
                  </span>
                </div>
                <Modal setVisible={setActive} visible={active} orientation='landscape' size='large'>
                  <iframe className={styles.sideVideoVideo} src={(active) ? youTubeEmbedUrl : ''}></iframe>
                </Modal>
              </>
              :
              null
            : null}
        </div>
      )}
      <BelowHeroCTA belowHeroCtaIntro={belowHeroCtaIntro} belowHeroCtaLink={belowHeroCtaLink} belowHeroCtaText={belowHeroCtaText} belowHeroDownloadLink={belowHeroDownloadLink} />
  
      {requestACallbackCta && (<Modal setVisible={setRequestACallbackModelActive} visible={requestACallbackModelActive} orientation='landscape' size='large'>
        <div>
          <h2>Request a callback</h2>
          <p>Would you like one of our team to contact you? No problem. Just fill out the details below and someone will be in touch shortly.</p>
          <small>Fields marked * are required</small>
          <Form
            endpoint={"https://getform.io/f/pbnvzdgb"}
            fields={requestACallbackFields !== false ? requestACallbackFields : RequestACallbackFormFields} />
        </div>
      </Modal>)}
    </>
  );
}

const BelowHeroCTA = ({ belowHeroCtaLink, belowHeroCtaText, belowHeroCtaIntro, belowHeroDownloadLink }) => {
  if ((belowHeroCtaLink && belowHeroCtaText) || belowHeroCtaIntro || belowHeroDownloadLink?.download?.publicUrl) {
    return (
      <div className={styles.belowHeroCTA}>
        {belowHeroCtaIntro && <h3>{belowHeroCtaIntro}</h3>}
        {((belowHeroCtaLink && belowHeroCtaText) || (belowHeroDownloadLink?.download?.publicUrl)) && (
          <Button
            colorData={`green`}
            typeData={`default`}
            href={belowHeroDownloadLink?.download?.publicUrl ?? belowHeroCtaLink}
            content={belowHeroCtaText}
            alt={belowHeroCtaText}
            additionalClasses={styles.belowHeroCTAButton}
            opensNewTab={belowHeroDownloadLink?.download?.publicUrl ? true : false}
          />
        )}
      </div>
    );
  }
};